import CachedIcon from "@mui/icons-material/Cached";
import {
  Box,
  Button,
  Container,
  IconButton,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import CourseTitleDateFilter from "components/CourseTitleDateFilter";
import ExpandableTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/ExpandableTable";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import { ReprocessSubmissionsDialog } from "components/ReprocessSubmissionsDialog";
import RequestResponseBox from "components/RequestResponseBox";
import { isValid } from "date-fns";
import { makeStyles } from "helpers/StyleHelper";
import getUserDisplayName from "helpers/userHelper";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import { useEffect, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import {
  CourseTitlesRequest,
  CourseTitleModel,
  ListRequestOfBaseFilter,
  ListRequestOfSubmissionRequestFilter,
  OrganizationUserDetails,
  SubmissionRequestFilter,
} from "services/KnowfullyAdminClient";
import { convertToUTC } from "utils/dateUtils";

const useStyles = makeStyles((theme: Theme) => ({
  reprocessSubmissions: {
    margin: "30px 0",
  },
  confirmationMessage: {
    margin: "15px 0",
  },
}));

const UserSubmissionsPage = () => {
  const { userId } = useParams<{ userId: string }>();
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const [order, setOrder] = useState<TableSortType>("desc");
  const [orderBy, setOrderBy] = useState("createdOn");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [reprocessDialogOpen, setReprocessDialogOpen] = useState(false);
  const [
    selectedCourseTitle,
    setSelectedCourseTitle,
  ] = useState<CourseTitleModel | null>(null);
  const [submissionDateFrom, setSubmissionDateFrom] = useState<
    Date | undefined
  >();
  const [submissionDateTo, setSubmissionDateTo] = useState<Date | undefined>();
  const {
    organizationUserService,
    submissionService,
    userDetailsUrl,
  } = useGlobalContext();

  const userResult = useQuery<OrganizationUserDetails>(
    ["getOrganizationUserDetails", userId],
    () => organizationUserService.getOrganizationUserDetails(userId!),
    {
      enabled: !!userId,
    },
  );

  const user = userResult.data;
  const title = t(
    { id: "userCreditSubmissions" },
    {
      name: getUserDisplayName(user, t({ id: "user" })),
    },
  );

  useTitle(title);

  const { isError, data, error, isLoading, refetch } = useQuery(
    [
      "getSubmissionRequests",
      page,
      rowsPerPage,
      orderBy,
      order,
      userId,
      selectedCourseTitle,
      submissionDateFrom,
      submissionDateTo,
    ],
    () =>
      submissionService.getSubmissionRequests(
        new ListRequestOfSubmissionRequestFilter({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortDescending: order === "desc",
          sortField: orderBy,
          parameters: new SubmissionRequestFilter({
            userId,
            searchText: selectedCourseTitle?.name || undefined,
            submissionDateFrom: isValid(submissionDateFrom)
              ? convertToUTC(submissionDateFrom)
              : undefined,
            submissionDateTo: isValid(submissionDateTo)
              ? convertToUTC(submissionDateTo)
              : undefined,
          }),
        }),
      ),
    {
      enabled: !!userId,
    },
  );

  useEffect(() => {
    setPage(0);
  }, [selectedCourseTitle, submissionDateFrom, submissionDateTo]);

  if (userResult.isLoading) return <Loader />;

  const rows = (data?.data ?? []).map((row) => ({
    ...row,
    isExpandable: !!row.requestObject || !!row.responseObject,
    renderExpandedData: () => (
      <RequestResponseBox
        requestObject={row.requestObject}
        responseObject={row.responseObject}
      />
    ),
  }));

  const cells: Array<TableCellType> = [
    {
      accessor: "courseTitle",
      customSortAccessor: "userDeliverablesCompletionDetail.courseTitle",
      width: 300,
      type: "text",
      label: t({ id: "titleLabel" }),
    },
    {
      accessor: "dateOfParticipation",
      type: "date",
      label: t({ id: "participationDate" }),
    },
    {
      accessor: "createdOn",
      type: "date",
      label: t({ id: "submissionDate" }),
    },
    {
      accessor: "courseNumber",
      type: "text",
      label: t({ id: "courseNumber" }),
    },
    {
      accessor: "authorityName",
      customSortAccessor: "authority.name",
      type: "text",
      label: t({ id: "authority" }),
    },
    {
      accessor: "licenseNumber",
      type: "text",
      label: t({ id: "licenseNumber" }),
    },
    {
      accessor: "licenseState",
      type: "text",
      label: t({ id: "licenseState" }),
    },
    {
      accessor: "professionName",
      noSort: true,
      type: "text",
      label: t({ id: "professionLabel" }),
    },
    {
      accessor: "statusName",
      customSortAccessor: "status.name",
      type: "text",
      label: t({ id: "statusLabel" }),
    },
  ];

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const fetchCourseTitles = (request: ListRequestOfBaseFilter) => {
    const listRequest = new CourseTitlesRequest({
      userId: userId,
      organizationId: user?.organizationId,
      ...request,
    });
    return submissionService.getSubmissionsCourseTitles(listRequest);
  };

  return (
    <Container>
      <RedirectBackButton
        redirectRoute={userDetailsUrl(userId)}
        labelKey="backToUser"
      />

      <h1>{title}</h1>

      <CourseTitleDateFilter
        cacheKey="submissions"
        fetchCourseTitles={fetchCourseTitles}
        selectedCourseTitle={selectedCourseTitle}
        onCourseTitleChange={setSelectedCourseTitle}
        dateFrom={submissionDateFrom}
        dateTo={submissionDateTo}
        onDateFromChange={setSubmissionDateFrom}
        onDateToChange={setSubmissionDateTo}
        dateRangeLabel={t({ id: "submissionDate" })}
      />

      <ExpandableTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows,
          loading: isLoading,
          dense: true,
          titleComponent: (
            <Box>
              {t({ id: "submissions" })}
              <Tooltip
                title={t({
                  id: "refresh",
                })}
              >
                <IconButton onClick={() => refetch()}>
                  <CachedIcon />
                </IconButton>
              </Tooltip>
            </Box>
          ),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          collapsable: true,
        }}
      />

      <ReprocessSubmissionsDialog
        open={reprocessDialogOpen}
        onClose={() => {
          refetch();
          setReprocessDialogOpen(false);
        }}
        userId={userId!}
      />

      <Box sx={styles.reprocessSubmissions}>
        <Typography variant="h5">
          <T id="reprocessSubmissionsLabel" />
        </Typography>
        <Typography sx={styles.confirmationMessage}>
          <T id="reprocessSubmissionsPreWarning" />
        </Typography>
        <Typography sx={styles.confirmationMessage}>
          <T id="reprocessSubmissionsWarning" />
        </Typography>
        <Typography sx={styles.confirmationMessage}>
          <T id="regenerateCertificatesConfirmationMessage" />
        </Typography>
        <Button
          variant="outlined"
          disabled={reprocessDialogOpen}
          onClick={() => setReprocessDialogOpen(true)}
        >
          <T id="reprocessSubmissionsLabel" />
        </Button>
      </Box>
    </Container>
  );
};

export default UserSubmissionsPage;
